import { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay } from "swiper";

import { CartContext } from "@context/CartProvider";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";
import useDeviceDetect from "@hooks/useDeviceDetect";
import { useShippingInfo } from "@hooks/useShippingInfo";
import { useCurrentFormatCurrency } from "@hooks/usePrices";
import useSalesColors from "@hooks/useSalesColors";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { getEligibleCartMetrics } from "@helpers/cart";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn, parseText } from "@helpers/general";

import DISCOUNT_TYPES from "@constants/DiscountTypes";
import { ComponentType } from "@constants/ComponentType";

import { applyCartDiscounts } from "@components/cartDiscounts";
import AnnouncementBarTimer from "@components/announcementBar/section/AnnouncementBarTimer";
import TieredOfferProgressBar from "@components/al_components/TieredOfferProgressBar";
import TieredOfferProgressBarGift from "@components/al_components/TieredOfferProgressBarGift";
import GiftOfferProgressBar from "@components/al_components/GiftOfferProgressBar";
import CheckoutCallout from "@components/CheckoutCallout";
import ProgressText from "@components/ProgressText";

import "swiper/swiper.min.css";
import "swiper/swiper-bundle.min.css";

SwiperCore.use([Autoplay]);

function Section({
  textArray,
  textIndex,
  addShippingThreshold,
  textNoThreshold,
  textPosition,
  enableRotatingText,
  timer,
  displayLuxeProgressBar,
}) {
  const salesColors = useSalesColors();
  const { isMobile } = useDeviceDetect();
  const shippingInfo = useShippingInfo();
  const { cart } = useContext(CartContext);
  const discountsInfo = useDiscountsInfo();
  const formatCurrency = useCurrentFormatCurrency();

  const shippingTreshold = addShippingThreshold ? shippingInfo?.freeShippingThreshold : null;
  const giftInfo = discountsInfo.find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE);
  const hasGiftDiscount = Boolean(giftInfo);

  const sideCartInfo = applyCartDiscounts(cart.lines, discountsInfo);
  const { lines } = sideCartInfo;

  const amountPercentInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );
  const quantityPercentInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.QUANTITY_PERCENT
  );
  const tieredPercentInfo = amountPercentInfo || quantityPercentInfo;
  const isQuantityTieredDiscount = tieredPercentInfo?.type === DISCOUNT_TYPES.QUANTITY_PERCENT;

  const percentOffInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.SITEWIDE_PERCENT
  );

  const shouldOverwriteText = textIndex === 0; // decide which section (0, 1, 2) should be overwritten with something else

  const tierDiscounts = useMemo(() => {
    if (!tieredPercentInfo) return [];

    const thresholds = isQuantityTieredDiscount
      ? tieredPercentInfo?.thresholdConditionQuantity
      : tieredPercentInfo?.thresholdConditionAmount;

    return (
      thresholds?.map((threshold, index) => [
        parseFloat(threshold),
        parseFloat(tieredPercentInfo.thresholdResultPercentage[index]),
      ]) || []
    );
  }, [tieredPercentInfo, isQuantityTieredDiscount]);

  const validDiscounts = tieredPercentInfo ? [tieredPercentInfo] : discountsInfo;
  const { currentEligibleValue } = getEligibleCartMetrics({
    lines: cart.lines,
    discountsToCheck: validDiscounts,
    excludeLuxeItems: true,
  });
  const firstTierReached = tieredPercentInfo && currentEligibleValue >= tierDiscounts[0]?.[0];
  const lastTierReached =
    tieredPercentInfo && currentEligibleValue >= tierDiscounts[tierDiscounts.length - 1]?.[0];

  const hasFreeGiftInCart = useMemo(
    () =>
      lines.some((line) =>
        line.attributes?.some(({ key, value }) => key === "_free_gift" && value === "true")
      ),
    [lines]
  );

  const firstText = textArray?.[0];
  const sectionStyle = {
    fontWeight: firstText?.fontWeight?.toLowerCase() || "normal",
    textColor: useResponsiveWithHydrationFix(
      firstText?.textColor?.mobile ?? "#000",
      firstText?.textColor?.desktop ?? "#000"
    ),
  };

  const showSection = textArray?.[0]?.text !== "null";

  let shouldShowTieredProgressBarGift = false;
  if (displayLuxeProgressBar && hasGiftDiscount) {
    if (tieredPercentInfo) {
      shouldShowTieredProgressBarGift = firstTierReached;
    } else {
      shouldShowTieredProgressBarGift = hasFreeGiftInCart;
    }
  }

  const shouldShowGiftProgressBar =
    displayLuxeProgressBar &&
    hasGiftDiscount &&
    !tieredPercentInfo &&
    currentEligibleValue > giftInfo?.freeShippingThreshold;
  const shouldShowProgressOnly = displayLuxeProgressBar && !hasGiftDiscount && firstTierReached;

  /**
   * Checkout callout conditions:
   *  1. AMOUNT_PERCENT/QUANTITY_PERCENT discount OR PERCENT_OFF => eligibleAmount > checkoutCalloutMinValue
   *  2. GIFT_WITH_PURCHASE and no tiered discount => when free gift was added to cart
   *
   *  Tiered discount takes priority because of the tiered progress bar
   */
  const minValueCheckoutCallout = parseFloat(
    (tieredPercentInfo || percentOffInfo)?.checkoutCalloutMinValue
  );

  const showCheckoutCallout =
    hasGiftDiscount && !tieredPercentInfo
      ? hasFreeGiftInCart
      : currentEligibleValue > minValueCheckoutCallout;

  let content = (
    <p
      style={{
        fontWeight: sectionStyle.fontWeight,
        color: sectionStyle.textColor,
      }}
    >
      {shippingTreshold === null && parseText(textArray[0].text ?? "")}
      {shippingTreshold === 0 && textNoThreshold}
      {shippingTreshold > 0 && (
        <span>{`${parseText(textArray[0].text ?? "")} ${formatCurrency(shippingTreshold)}`}</span>
      )}
    </p>
  );

  if (shouldOverwriteText) {
    content = (
      <ProgressText
        firstTierReached={firstTierReached}
        lastTierReached={lastTierReached}
        freeGiftMinAmount={giftInfo?.freegift?.minAmount}
        freeGiftDiscount={sideCartInfo.discountFreeGift}
        content={content}
        componentType={ComponentType.ANNOUNCEMENT_BAR}
      />
    );
  }

  if (shouldOverwriteText && showCheckoutCallout) {
    content = "";
  }

  if (shouldShowTieredProgressBarGift) {
    content = (
      <TieredOfferProgressBarGift
        componentType={ComponentType.ANNOUNCEMENT_BAR}
        saleColors={salesColors}
        eligibleAmount={currentEligibleValue}
      />
    );
  }

  if (shouldShowProgressOnly) {
    content = (
      <TieredOfferProgressBar
        componentType={ComponentType.ANNOUNCEMENT_BAR}
        saleColors={salesColors}
        eligibleAmount={currentEligibleValue}
      />
    );
  }

  if (shouldShowGiftProgressBar) {
    content = (
      <GiftOfferProgressBar
        componentType={ComponentType.ANNOUNCEMENT_BAR}
        saleColors={salesColors}
        eligibleAmount={currentEligibleValue}
      />
    );
  }

  const checkoutCalloutSectionIndex = isMobile ? 0 : 1;

  if (showCheckoutCallout && textIndex === checkoutCalloutSectionIndex) {
    content = (
      <CheckoutCallout componentType={ComponentType.ANNOUNCEMENT_BAR} saleColors={salesColors} />
    );
  }

  if (enableRotatingText && !shouldShowProgressOnly) {
    return (
      <div
        className="typo-micro flex w-full justify-between items-center self-center grow desktop:justify-unset"
        style={{
          textAlign: textPosition,
          color: salesColors.tierTextColorLuxe,
        }}
      >
        <Swiper
          enabled
          spaceBetween="80"
          slidesPerView="1"
          centeredSlides
          speed="5000"
          loop
          autoplay={{
            delay: 4000,
            disableOnInteraction: true,
            pauseOnMouseEnter: false,
          }}
        >
          {textArray.map((text, i) => (
            <SwiperSlide
              key={`${text.text}-${i}`}
              style={{
                fontWeight: text.fontWeight,
                color: isMobile ? text.textColor.mobile : text.textColor.desktop,
                justifySelf: isMobile ? "inherit" : textPosition?.toLowerCase(),
              }}
              className="typo-micro"
              data-testid="swiper-slide"
            >
              {parseText(text.text)}
            </SwiperSlide>
          ))}
        </Swiper>
        {timer?.enabled && <AnnouncementBarTimer timer={timer} />}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "typo-micro w-full flex justify-between items-center self-center desktop:justify-unset",
        displayLuxeProgressBar ? "flex-[2]" : "flex-1",
        lastTierReached && "px-[10px]"
      )}
      style={{
        justifyContent: textPosition,
        textAlign: textPosition,
        visibility: showSection ? "inherit" : "hidden",
        color: salesColors.tierTextColorLuxe,
      }}
      data-testid="section"
    >
      {shouldShowTieredProgressBarGift || shouldShowProgressOnly || shouldShowGiftProgressBar ? (
        <div className={cn("w-full", lastTierReached ? "text-center" : "text-left")}>{content}</div>
      ) : (
        content
      )}
      {timer?.enabled && (
        <>
          &nbsp;
          <AnnouncementBarTimer timer={timer} />
        </>
      )}
    </div>
  );
}

export default withALErrorBoundary({
  name: "Section",
  priority: "P2",
})(Section);

Section.propTypes = {
  textArray: PropTypes.arrayOf(
    PropTypes.shape({
      textColor: PropTypes.shape({
        mobile: PropTypes.string,
        desktop: PropTypes.string,
      }),
      borderColor: PropTypes.shape({
        mobile: PropTypes.string,
        desktop: PropTypes.string,
      }),
      fontWeight: PropTypes.string,
      text: PropTypes.string,
    })
  ),
  addShippingThreshold: PropTypes.bool,
  textNoThreshold: PropTypes.string,
  textPosition: PropTypes.string,
  enableRotatingText: PropTypes.bool,
  timer: PropTypes.shape({
    enabled: PropTypes.bool,
  }),
  displayLuxeProgressBar: PropTypes.bool,
  textIndex: PropTypes.number,
};
